<template>
    <page-loading v-if="loading"></page-loading>
    <form-container v-else>
        <step-form-heading v-if="!props.snippetId"
            :icon="faClipboard">
            Snippet Template

            <template v-slot:description>
                Create a Snippet and share it with Groups.
            </template>
        </step-form-heading>

        <page-header v-else
            class="mb-6 lg:mb-12">
            <template v-slot:subtitle>
                <a href="/messaging/snippets/templates"
                    class="no-underline! text-sky-600! text-base font-normal">
                    <font-awesome-icon widthAuto class="mr-1"
                        :icon="faArrowLeft"></font-awesome-icon>
                    All Snippet Templates
                </a>
            </template>

            {{ snippet.title }}
        </page-header>

        <div class="grid grid-cols-12">
            <page-sidebar v-if="props.snippetId"
                :links="[
                    { href: `/messaging/snippets/templates/${props.snippetId}`, label: 'Details' },
                ]">
            </page-sidebar>

            <form-wrapper
                :sections="[
                    { id: 'title', label: 'Name' },
                    { id: 'description', label: 'Description' },
                    { id: 'snippet', label: 'Snippet' },
                    { id: 'groups', label: 'Groups' },
                ]"
                :hideNav="Boolean(props.snippetId)"
            >


                <vx-expandable-card id="title">
                    <template v-slot:header> Name </template>

                    <template v-slot:content>
                        <vx-input v-model="snippet.title"
                            data-test="title"
                            :error="displayFirstError(validation.title)"
                            autofocus
                            size="lg"
                            :maxlength="120"></vx-input>
                    </template>
                </vx-expandable-card>

                <vx-expandable-card id="description">
                    <template v-slot:header> Description </template>

                    <template v-slot:content>
                        <vx-input v-model="snippet.description"
                            data-test="description"
                            :error="displayFirstError(validation.description)"
                            autofocus
                            autoresize
                            :rows="3"
                            size="lg"
                            :maxlength="2000"></vx-input>
                    </template>
                </vx-expandable-card>

                <vx-expandable-card id="snippet">
                    <template v-slot:header> Snippet </template>
                    <template v-slot:content>
                        <vx-label>Message</vx-label>
                        <message-builder ref="messageBuilderRef"
                            v-model="snippet.body"
                            v-model:mediaUrl="snippet.media_url"
                            v-model:mediaContentType="snippet.media_content_type"
                            disableSnippets
                            testable
                            :error="displayFirstError(validation.body)"></message-builder>
                    </template>
                </vx-expandable-card>


                <snippet-template-form-group-access id="groups" :snippetId="props.snippetId ? Number(props.snippetId) : null"
                    v-model:groupFilters="snippet.group_filters"
                    v-model:groupCollectionId="snippet.group_collection_id">
                </snippet-template-form-group-access>


                <vx-sticky-footer>
                    <vx-button href="/messaging/snippets/templates"
                        class="order-3 lg:w-20 lg:order-1"
                        size="lg"
                        color="muted-dark">
                        Cancel
                    </vx-button>
                    <vx-button @click="submit()"
                        data-test="submit"
                        :loading="processing"
                        type="button"
                        class="order-1 lg:grow lg:order-3"
                        size="lg"
                        color="primary">
                        {{ props.snippetId ? 'Update' : 'Create' }} Snippet Template
                    </vx-button>
                </vx-sticky-footer>

            </form-wrapper>
        </div>
    </form-container>
</template>

<script setup>
import { faArrowLeft, faClipboard, faSquareArrowUpLeft } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { VxLabel, VxButton, VxExpandableCard, VxInput, VxStickyFooter } from '@voxie/frontend-components';
import { maxLength, required } from '@vuelidate/validators';
import { ref } from 'vue';
import PageHeader from '~/components/general/PageHeader.vue';
import PageSidebar from '~/components/general/PageSidebar.vue';
import PageLoading from '~/components/general/PageLoading.vue';
import FormContainer from '~/components/general/FormContainer.vue';
import { useFormSubmission } from '~/composables/useFormSubmission';
import { displayFirstError } from '~/utils/validation';
import StepFormHeading from '~/components/general/StepFormHeading.vue';
import { snippetsClient } from '~/services';
import MessageBuilder from '~/components/general/message-builder/MessageBuilder.vue';
import SnippetTemplateFormGroupAccess from './SnippetTemplateFormGroupAccess.vue';
import FormWrapper from '../general/FormWrapper.vue';

const teamId = Spark.state.currentTeam.id;
const loading = ref(false);

const defaultData = () => {
    return {
        title: '',
        description: '',
        body: '',
        group_filters: [],
        group_collection_id: null,
    }
}

const snippet = ref(defaultData());

const props = defineProps({
    snippetId: {
        type: [Number, String],
    }
})

if (props.snippetId) {
    loading.value = true;
    snippetsClient.getSnippet({ teamId }, props.snippetId)
        .then(response => {
            snippet.value = response.data
            loading.value = false;
        });
}

const { validation, submit, processing } = useFormSubmission({
    data: snippet,
    validation: {
        title: {
            required,
            maxLength: maxLength(120)
        },
        description: {
            required,
            maxLength: maxLength(2000)
        },
        body: {
            required,
            maxLength: maxLength(1600)
        }
    },
    submit: async () => {
        const snippetData = { ...snippet.value, is_template: true };

        if (snippetData.group_collection_id === null) {
            delete snippetData.group_collection_id;
        }

        if (props.snippetId) {
            await snippetsClient.updateSnippet({ teamId }, props.snippetId, snippetData);
        } else {
            await snippetsClient.createSnippet({ teamId }, snippetData);
        }
    },
    preserveScroll: () => props.snippetId,
    reset: () => {
        if (!props.snippetId) {
            snippet.value = defaultData();
        }
    },
    notification: () => ({
        message: `Snippet Template ${props.snippetId ? 'updated' : 'created'}`,
        action: {
            icon: faSquareArrowUpLeft,
            label: 'Back to Snippet Templates',
            href: '/messaging/snippets/templates',
        },
    })
});

</script>
